import { PropsWithChildren, useMemo, useRef } from 'react'

import { PlusOutlined } from '@ant-design/icons'
import { Button, Upload, UploadFile as AntdUploadFile, UploadProps } from 'antd'
import { createStyles } from 'antd-style'
import { isBoolean, isNil } from 'lodash'

import { OssFileDetail } from '@eastcoal/api/nexus/interface'

import { useAuthorizedResource } from '@eastcoal/auth/hooks/useAuthorizedResource'

import { ObjectAcl } from '../../enums/ObjectAcl'
import { useFeedback } from '../../hooks/useFeedback'
import { downloadUrl } from '../../utils/filesaver'
import { filesize } from '../../utils/filesize'
import { wrapUploadFileApi } from '../../utils/upload'
import { DataError } from '../DataError'
import { ImageCropper } from '../ImageCropper'
import { ImageCropperRef } from '../ImageCropper/interface'
import { PreviewFile } from '../PreviewFile'
import { PreviewFileRef } from '../PreviewFile/interface'

import { useUploadResult } from './hooks/useResult'
import { UploadFileProps, UploadRequestOptions } from './interface'

const useStyles = createStyles(({ prefixCls, token }) => ({
  wrapper: {
    display: 'inline-block',
    width: '100%',
    maxWidth: '400px'
  },
  uploadFile: {
    [`&.${prefixCls}-upload-wrapper:not(.${prefixCls}-upload-picture-card-wrapper) .${prefixCls}-upload-list .${prefixCls}-upload-list-item`]:
      {
        display: 'inline-flex',
        width: '100%',
        padding: `${token.paddingSM}px ${token.paddingXS}px`,

        [`& .${prefixCls}-upload-list-item-action`]: {
          marginInlineStart: token.marginXS
        },
        [`& .anticon-delete:hover`]: {
          color: token.red
        }
      }
  }
}))

// 2M
const MAX_FILE_SIZE = 2097152

const customRequest = async ({
  file,
  objectAcl,
  onError,
  onSuccess
}: UploadRequestOptions & {
  objectAcl?: ObjectAcl
}) => {
  try {
    const res = await wrapUploadFileApi(
      { file, objectAcl },
      {
        silence: true
      }
    )
    onSuccess?.(res)
  } catch (error: any) {
    onError?.(error)
  }
}

const defaultShowUploadList: UploadProps['showUploadList'] = {
  showDownloadIcon: true,
  showPreviewIcon: true,
  showRemoveIcon: true
}

export const UploadFile = ({
  wrapperClassName,
  wrapperStyle,

  /* -------------------------------------------------------------------------- */
  /*                              UploadFile Props                              */
  /* -------------------------------------------------------------------------- */
  uuid,
  onlyAdministrator,
  objectAcl,
  cropper,
  maxFileSize = MAX_FILE_SIZE,
  readPretty,
  children,

  /* -------------------------------------------------------------------------- */
  /*                            useUploadResult Props                           */
  /* -------------------------------------------------------------------------- */
  thumbnail,
  thumbnailStyle,
  value,
  onChange,
  onDetail,

  /* -------------------------------------------------------------------------- */
  /*                                Upload Props                                */
  /* -------------------------------------------------------------------------- */
  className,
  style,
  multiple = true,
  listType = 'text',
  showUploadList,
  accept,
  disabled,
  maxCount = 10,
  openFileDialogOnClick
}: PropsWithChildren<UploadFileProps>) => {
  const { message } = useFeedback()
  const { error, loading, reload, result, receiveFile } = useUploadResult({
    thumbnail,
    thumbnailStyle,
    value,
    onChange,
    onDetail
  })

  const fileList = useMemo(() => Object.values(result), [result])

  const previewRef = useRef<PreviewFileRef>(null)

  const handlePreview: UploadProps['onPreview'] = file => {
    const item = result[file.uid]
    if (item) {
      previewRef.current?.open(item, fileList)
    } else {
      message.warning('当前文件无法预览')
    }
  }

  const handleDownload: UploadProps['onDownload'] = file => {
    if (file.url) {
      downloadUrl(file.url, file.name)
    }
  }

  const updateFile = async (options: {
    file: AntdUploadFile<OssFileDetail>
  }) => {
    if (isNil(options.file.status)) {
      return
    }
    receiveFile({
      ...options,
      replaced: maxCount === 1
    })
  }

  const cropperRef = useRef<ImageCropperRef>(null)
  const cropperProps = isBoolean(cropper) ? undefined : cropper

  const handleBeforeUpload: UploadProps['beforeUpload'] = async file => {
    let finalFile: typeof file | Blob = file

    if (cropper && cropperRef.current) {
      finalFile = await cropperRef.current.crop(URL.createObjectURL(file))
    }

    // 校验文件大小
    if (!isNil(maxFileSize)) {
      if (finalFile.size > maxFileSize) {
        const msg = `当前文件 ${filesize(file.size)} 超过最大限制 ${filesize(
          maxFileSize
        )}`
        if (showUploadList !== false && maxCount > 1) {
          updateFile({
            file: {
              name: file.name,
              uid: file.uid,
              size: file.size,
              type: file.type,
              fileName: file.name,
              url: URL.createObjectURL(finalFile),
              originFileObj: file,
              status: 'error',
              error: new Error(msg)
            }
          })
        } else {
          message.warning(msg)
        }
        return false
      }
    }
    return finalFile
  }

  const finalDisabled = disabled || readPretty
  const finalShowUploadList =
    showUploadList === false
      ? false
      : showUploadList === true
        ? {
            ...defaultShowUploadList,
            showRemoveIcon: !finalDisabled
          }
        : {
            ...defaultShowUploadList,
            ...showUploadList,
            showRemoveIcon: finalDisabled
              ? false
              : (showUploadList?.showRemoveIcon ?? true)
          }

  const handlerNode =
    readPretty || fileList.length >= maxCount
      ? null
      : (children ?? (
          <Button
            disabled={finalDisabled}
            icon={<PlusOutlined />}
            loading={loading}
          >
            上传附件
          </Button>
        ))

  const authorized = useAuthorizedResource(uuid, onlyAdministrator)

  const { cx, styles } = useStyles()

  return authorized ? (
    <div className={cx(styles.wrapper, wrapperClassName)} style={wrapperStyle}>
      <Upload
        accept={accept}
        beforeUpload={handleBeforeUpload}
        className={cx(styles.uploadFile, className)}
        disabled={finalDisabled}
        fileList={fileList}
        listType={listType}
        maxCount={maxCount}
        multiple={multiple && maxCount > 1}
        openFileDialogOnClick={openFileDialogOnClick}
        showUploadList={finalShowUploadList}
        style={style}
        customRequest={options =>
          customRequest({
            objectAcl,
            ...options
          })
        }
        onChange={updateFile}
        onDownload={handleDownload}
        onPreview={handlePreview}
      >
        {handlerNode}
      </Upload>
      <ImageCropper ref={cropperRef} {...cropperProps} />
      <DataError error={error} reload={reload} />
      <PreviewFile ref={previewRef} />
    </div>
  ) : null
}
